import axios from "axios";

type IdentityErrorContext = "send" | "verify" | "google";

const DEFAULT_MESSAGES: Record<IdentityErrorContext, string> = {
  send: "Could not send the code. Try again.",
  verify: "Login failed. Please try again later.",
  google: "Unable to start Google login.",
};

export const getIdentityErrorMessage = (
  error: unknown,
  context: IdentityErrorContext,
): string => {
  const fallback = DEFAULT_MESSAGES[context];

  try {
    if (!axios.isAxiosError(error)) {
      if (error instanceof Error && error.message) return error.message;
      return fallback;
    }

    const status = error.response?.status;
    const message = String(
      error.response?.data?.message ||
        error.response?.data?.result?.message ||
        "",
    ).trim();
    const normalized = message.toLowerCase();

    if (status === 429) {
      return "Please wait before requesting another code.";
    }
    if (status === 502) {
      return "Could not send the code. Try again.";
    }
    if (status === 403 || status === 503) {
      return "Verification failed. Refresh the page and try again.";
    }

    if (
      normalized.includes("invalid or expired otp") ||
      normalized.includes("otp has expired") ||
      normalized.includes("otp already used")
    ) {
      return "Invalid or expired code. Request a new one.";
    }
    if (normalized.includes("too many invalid")) {
      return "Too many attempts. Request a new code.";
    }
    if (normalized.includes("login failed")) {
      return "Login failed. Please try again later.";
    }

    if (context === "google" && message) return message;
    if (message) return message;
    return fallback;
  } catch (_error) {
    return fallback;
  }
};

export const getRetryAfterSeconds = (error: unknown): number => {
  try {
    if (!axios.isAxiosError(error)) return 60;
    const header = error.response?.headers?.["retry-after"];
    const parsed = Number(header);
    if (Number.isFinite(parsed) && parsed > 0) return Math.ceil(parsed);
    return 60;
  } catch (_error) {
    return 60;
  }
};

export const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible = local.slice(0, 1);
  return `${visible}***@${domain}`;
};

export const maskPhone = (phone: string, countryCode: string): string => {
  const last = phone.slice(-4);
  return `${countryCode} •••••${last}`;
};

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const isValidPhone = (phone: string): boolean =>
  /^\d{10}$/.test(phone.trim());

export const normalizeCountryCode = (countryCode: string): string => {
  const trimmed = countryCode.trim() || "+91";
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
};
